
import { computed, ref, useAsync, useContext } from '@nuxtjs/composition-api';
import UserInQuarantineError from '@/components/TlmCheckout/StepValidators/UserInQuarantineError';
import ListGroup from '@/components/TlmCheckout/Subscription/ChangeSubscription/ListGroup';
import RemovedServices from '@/components/TlmCheckout/Subscription/ChangeSubscription/RemovedServices';
import AuthenticateUserAlt from '@/components/TlmCheckout/Subscription/ChangeSubscription/AuthenticateUserAlt';
import NegativeBalance from '@/components/TlmCheckout/Subscription/ChangeSubscription/NegativeBalance';
import BindingError from '@/components/TlmCheckout/Subscription/ChangeSubscription/BindingError';
import OwnerError from '@/components/TlmCheckout/Subscription/ChangeSubscription/OwnerError';
import ChangeInProgressError from '@/components/TlmCheckout/Subscription/ChangeSubscription/ChangeInProgressError'
import { CUSTOMER_SUBSCRIPTION_TYPE, CUSTOMER_TYPE } from '~/constants/CheckoutFlow';
import { ERROR_CODE } from '~/constants/ErrorCodes';
import { VALIDATION_STATUS } from '~/constants/ValidationStatus';
import useCheckout from '~/composable/checkout/useCheckout';
import useDiscounts from '~/composable/discounts/useDiscounts';
import useTlmCart from '~/composable/useTlmCart';
import useSubscriptions from '@/composable/checkout/validations/useSubscriptions';
import useChangeSubscriptionValidation from '~/composable/useChangeSubscriptionValidation';
import MultipleBindingError from '~/components/TlmCheckout/Subscription/ChangeSubscription/MultipleBindingError.vue';

export default {
  name: 'TlmCheckoutSubscriptionKeepSubscription',
  components: { MultipleBindingError, RemovedServices, ListGroup, AuthenticateUserAlt, UserInQuarantineError, ChangeInProgressError, OwnerError, BindingError, NegativeBalance },
  setup() {
    const { app } = useContext()

    const keepSubscriptionNumber = ref(null)
    const loading = ref(false);
    const { state } = useCheckout(app)
    const { cartID, isEsimOnlyDevice } = useTlmCart(app);

    const { validateKeepSubscriptionChoice, getLatestDiscountCalculated } = useDiscounts(app);
    const { validateChangeSubscription } = useSubscriptions(app)
    const {
      removedServices,
      onSubscriptionSelected,
      onSubscriptionsReady,
      isLoggedIn,
      hasError,
      showUserInQuarantineError,
      showNegativeBalanceError,
      showBindingError,
      showMultipleBindingError,
      showCvrNoMatch,
      showOwnerError,
      showChangeInProgressError,
      errorCode,
      checkNegativeBalance,
      checkNoMatch,
      checkOwnerError,
      checkBinding,
      checkMultipleBinding,
      checkChangeInProgressError,
      checkUserInQuarantineError,
      formatPayload
    } = useChangeSubscriptionValidation(app)

    const subscription = state.subscription
    const customerInfo = state.customerInfo
    const { changeSubscription } = subscription;
    const { menu } = customerInfo
    const inProgress = ref(false);
    const keepSubscriptionValidationFailure = ref(false);
    const changeSubscriptionValidationFailure = ref(false);

    const mappedErrorFlags = [
      showNegativeBalanceError,
      showBindingError,
      showMultipleBindingError,
      showOwnerError,
      showChangeInProgressError,
      showUserInQuarantineError
    ]

    const hasMappedErrorUi = () => {
      return mappedErrorFlags.some(errorRef => errorRef.value)
    }

    const getValidationErrorList = (res) => {
      if (Array.isArray(res.errors)) {
        return res.errors
      }
      if (Array.isArray(res.actionErrors)) {
        return res.actionErrors
      }
      return []
    }

    const validationFailed = (res) => {
      if (!res) {
        return true
      }
      return Boolean(res.hasErrors) ||
        res.actionStatus === VALIDATION_STATUS.ERROR ||
        getValidationErrorList(res).length > 0
    }

    const blockWithChangeInProgressError = () => {
      changeSubscriptionValidationFailure.value = true
      showChangeInProgressError.value = true
      hasError.value = true
    }

    const applyChangeSubscriptionValidationResult = (res) => {
      if (!res) {
        blockWithChangeInProgressError()
        return
      }

      checkNegativeBalance(res)
      checkBinding(res)
      checkMultipleBinding(res)
      checkOwnerError(res)
      checkChangeInProgressError(res)
      checkUserInQuarantineError(res)
      checkNoMatch(res)

      if (validationFailed(res)) {
        changeSubscriptionValidationFailure.value = true
        // Any blocking validation without a mapped alert → show pending-change message
        if (!hasMappedErrorUi()) {
          showChangeInProgressError.value = true
          hasError.value = true
        }
        return
      }
      changeSubscriptionValidationFailure.value = false
    }

    const showContinueBtn = computed(() => {
      return !changeSubscriptionValidationFailure.value && !hasMappedErrorUi()
    })

    const isKeepSubPendingChangeCode = (code) => {
      return code === ERROR_CODE.KEEP_SUB_PENDING_CHANGE
    }

    const hasPendingInDiscountSubscriptions = () => {
      const subscriptionsDTO = getLatestDiscountCalculated.value?.subscriptionsDTO
      if (!subscriptionsDTO) {
        return false
      }
      const isBundleType = (type) => {
        const normalized = String(type || '').toUpperCase()
        return normalized === 'BUNDLE' || normalized === 'MIX_BUNDLE'
      }
      const isPendingStatus = (status) => {
        const normalized = String(status || '').toUpperCase().replace(/-/g, '_')
        return normalized === 'PENDING_SUBSCRIPTION' ||
          normalized === 'PENDINGSUBSCRIPTION' ||
          normalized === 'PENDING_CANCELLATION' ||
          normalized === 'PENDINGCANCELLATION'
      }
      const subscriptionList = subscriptionsDTO.subscriptionList || []
      const hasPendingInList = subscriptionList.some((subscription) => {
        return isBundleType(subscription.productOfferingType) &&
          isPendingStatus(subscription.subscriptionStatus)
      })
      if (hasPendingInList) {
        return true
      }
      const pendingStates = subscriptionsDTO.pendingStateServiceList || []
      return pendingStates.some((pending) => isBundleType(pending.productOfferingType))
    }

    const validateAndSubscriptionsReady = (res) => {
      if (res?.userName === keepSubscriptionNumber.value) {
        onSubscriptionsReady(res)
      } else {
        keepSubscriptionValidationFailure.value = true
      }
    }

    const runPrivateChangeSubscriptionValidation = async (phoneNumber) => {
      const res = await validateChangeSubscription(formatPayload(phoneNumber))
      applyChangeSubscriptionValidationResult(res)
    }

    const applyKeepSubscriptionChoiceResult = async (response) => {
      keepSubscriptionNumber.value = response?.data?.additionalObject
      if (response?.data?.valid) {
        keepSubscriptionValidationFailure.value = false
        if (hasPendingInDiscountSubscriptions()) {
          blockWithChangeInProgressError()
          return
        }
        if (menu.value === CUSTOMER_TYPE.PRIVATE) {
          await runPrivateChangeSubscriptionValidation(response?.data?.additionalObject)
        }
        return
      }
      if (isKeepSubPendingChangeCode(response?.data?.code)) {
        blockWithChangeInProgressError()
        return
      }
      keepSubscriptionValidationFailure.value = true
      keepSubscriptionNumber.value = null
    }

    useAsync(async () => {
      inProgress.value = true
      keepSubscriptionNumber.value = null
      try {
        const response = await validateKeepSubscriptionChoice(cartID.value)
        await applyKeepSubscriptionChoiceResult(response)
      } catch (err) {
        blockWithChangeInProgressError()
      }
      inProgress.value = false
    }, 'discount')
    const formSubmitted = () => {
      loading.value = true
      subscription.menu.value = CUSTOMER_SUBSCRIPTION_TYPE.KEEP_SUBSCRIPTION
      $nuxt.$emit('formSubmitted')
      setTimeout(() => {
        loading.value = false
      }, 2000)
    }
    return {
      showMultipleBindingError,
      validateAndSubscriptionsReady,
      keepSubscriptionNumber,
      hasError,
      changeSubscription,
      removedServices,
      onSubscriptionSelected,
      CUSTOMER_TYPE,
      onSubscriptionsReady,
      isLoggedIn,
      menu,
      inProgress,
      checkNoMatch,
      showUserInQuarantineError,
      showNegativeBalanceError,
      showBindingError,
      showCvrNoMatch,
      showOwnerError,
      showChangeInProgressError,
      showContinueBtn,
      errorCode,
      loading,
      formSubmitted,
      subscriptions: computed(() => {
        return changeSubscription.subscriptions.filter(item => item.phoneNumber === keepSubscriptionNumber.value)
      }),
      keepSubscriptionValidationFailure,
      changeSubscriptionValidationFailure,
      isEsimOnlyDevice
    }
  }
}
